import React, { useState } from 'react';
import {
    Box,
    Text,
    Paper,
    Tabs,
    Select,
    Badge,
    SimpleGrid,
    Divider,
    Button,
    Tooltip,
    Table,
    Accordion,
    Stack,
    Alert,
    ActionIcon,
    Group
} from '@mantine/core';
import {
    IconInfoCircle,
    IconSchema,
    IconLink,
    IconCode,
    IconCopy,
    IconChevronDown,
    IconList
} from '@tabler/icons-react';
import { JsonViewer } from './JsonViewer';
import { ScimAceEditor } from './ScimAceEditor';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { Tier2SchemaTabView } from './Tier2SchemaTabView';
import { ScimReferenceDisplay } from './ScimReferenceDisplay';
import EntitlementsService from '../../services/EntitlementsService';
import { getTargetEntitlementsList } from '../../types/EntityEntitlements';
import { debugLog } from '../../utils/debugLog';

interface SharedDebugComponentProps {
    entity: any;
    entityType?: string;
    title?: string;
    /** Tier3 entitlement (GET .../Entitlements/entity/{schemaEntityId}/{targetId}); used by Drill tab for per-attribute entitlements. */
    targetEntitlement?: any | null;
    /** When set, render a single tab's content without the internal tab strip. */
    forcedTab?: SharedDebugTabKey;
    /** Removes the outer paper wrapper; useful when embedding one debug view inside another tab set. */
    bare?: boolean;
}

export type SharedDebugTabKey = 'basicInfo' | 'itemized' | 'overview' | 'drill' | 'ent' | 'rawJson';

// Error boundary component for individual tabs
class TabErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback: React.ReactNode },
    { hasError: boolean }
> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(_error: any) {
        return { hasError: true };
    }

    componentDidCatch(error: any, errorInfo: any) {
        console.error('Tab error boundary caught error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}

const RAW_JSON_TAB_INDEX = 5;
const DEBUG_TAB_VALUES: Record<SharedDebugTabKey, string> = {
    basicInfo: '0',
    itemized: '1',
    overview: '2',
    drill: '3',
    ent: '4',
    rawJson: String(RAW_JSON_TAB_INDEX)
};

/** Tier3: show attributes that have Binding (finalized API) or legacy Bindings / top-level props. */
const LEGACY_BINDING_PROPERTY_NAMES = ['RegEx', 'Required', 'AllowedValues'] as const;

function hasAnyBindingProperty(item: any): boolean {
    if (!item || typeof item !== 'object') return false;
    const binding = item.Binding ?? item.binding ?? item.Attribute?.Binding ?? item.Attribute?.binding ?? item.attribute?.Binding ?? item.attribute?.binding;
    if (binding != null && typeof binding === 'object' && Object.keys(binding).length > 0) return true;
    const bindings = item.Attribute?.Bindings ?? item.Attribute?.bindings ?? item.Bindings ?? item.bindings;
    if (bindings != null && typeof bindings === 'object' && Object.keys(bindings).length > 0) return true;
    const attr = item.Attribute ?? item.attribute ?? {};
    const has = (key: string) => {
        const v = item[key] ?? item[key.charAt(0).toLowerCase() + key.slice(1)] ?? attr?.[key] ?? attr?.[key.charAt(0).toLowerCase() + key.slice(1)];
        if (key === 'Required') return v === true;
        if (key === 'AllowedValues') return Array.isArray(v) && v.length > 0;
        return v != null && v !== '';
    };
    return LEGACY_BINDING_PROPERTY_NAMES.some((name) => has(name));
}

function toBindingView(item: any): Record<string, unknown> {
    const attr = item?.Attribute ?? item?.attribute ?? {};
    const out: Record<string, unknown> = {
        Id: attr.Id ?? attr.id ?? item?.Id ?? item?.id,
        DisplayName: attr.DisplayName ?? attr.displayName ?? item?.DisplayName ?? item?.displayName,
        Type: attr.Type ?? attr.type ?? item?.Type ?? item?.type
    };
    const binding = item?.Binding ?? item?.binding ?? attr?.Binding ?? attr?.binding;
    if (binding != null && typeof binding === 'object') {
        out.Binding = binding;
        return out;
    }
    const bindings = attr?.Bindings ?? attr?.bindings ?? item?.Bindings ?? item?.bindings;
    if (bindings != null && typeof bindings === 'object') {
        out.Bindings = bindings;
        return out;
    }
    const regEx = item?.RegEx ?? item?.regex ?? attr?.RegEx ?? attr?.regex;
    if (regEx != null && regEx !== '') out.RegEx = regEx;
    if (item?.Required === true || attr?.Required === true) out.Required = true;
    const allowed = item?.AllowedValues ?? item?.allowedValues ?? attr?.AllowedValues ?? attr?.allowedValues;
    if (Array.isArray(allowed) && allowed.length > 0) out.AllowedValues = allowed;
    return out;
}

export const SharedDebugComponent: React.FC<SharedDebugComponentProps> = ({
    entity,
    entityType = 'Entity',
    title,
    targetEntitlement = null,
    forcedTab,
    bare = false
}) => {
    const [activeTab, setActiveTab] = useState<string | null>('0');
    const [entSubTab, setEntSubTab] = useState<string | null>('0'); // Ent tab: 0 = Tier 1, 1 = Tier 2, 2 = Tier 3, 3 = Bindings
    const [selectedBindingId, setSelectedBindingId] = useState<string>(''); // '' = show all bindings
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedSubPath, setSelectedSubPath] = useState(''); // '' = view full; for arrays = index, for objects = nested key
    const [error, setError] = useState<string | null>(null);

    const effectiveEntityType = typeof entityType === 'string' ? entityType : 'Entity';
    const entityId = entity?.Id ?? entity?.id;
    const effectiveActiveTab = forcedTab ? DEBUG_TAB_VALUES[forcedTab] : activeTab;
    // Add error boundary functionality
    React.useEffect(() => {
        setError(null);
    }, [entity, activeTab]);

    const handleTabChange = (value: string | null) => {
        setActiveTab(value);
        setSelectedProperty('');
        setSelectedSubPath('');
    };

    const handleCopyJson = async () => {
        try {
            const seen = new WeakSet();
            const replacer = (_key: string, value: any) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) {
                        return '[Circular Reference]';
                    }
                    seen.add(value);
                }
                if (typeof value === 'function') return '[Function]';
                if (typeof value === 'undefined') return '[Undefined]';
                if (typeof value === 'symbol') return '[Symbol]';
                return value;
            };

            const jsonString = JSON.stringify(entity, replacer, 2);
            await copyToClipboard(jsonString);
            debugLog('✅ JSON copied to clipboard');
        } catch (error) {
            console.error('❌ Failed to copy JSON to clipboard:', error);
        }
    };

    // Helper functions
    const getSchemas = () => {
        try {
            if (!entity || !entity.schemas || !Array.isArray(entity.schemas)) {
                return [];
            }
            return entity.schemas;
        } catch (error) {
            console.error('Error getting schemas:', error);
            return [];
        }
    };

    const getObjectReferencesCount = () => {
        try {
            if (!entity || typeof entity !== 'object') {
                return 0;
            }

            let count = 0;
            for (const [key, value] of Object.entries(entity)) {
                if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                    count++;
                }
            }
            return count;
        } catch (error) {
            console.error('Error getting object references count:', error);
            return 0;
        }
    };

    const getObjectReferences = () => {
        try {
            if (!entity || typeof entity !== 'object') {
                return [];
            }

            const references: string[] = [];
            for (const [key, value] of Object.entries(entity)) {
                if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                    references.push(key);
                }
            }
            return references;
        } catch (error) {
            console.error('Error getting object references:', error);
            return [];
        }
    };

    /** Top-level properties only (no array indices, no nested keys); used for Drill first dropdown. */
    const getTopLevelProperties = (obj: any): Array<{ path: string, type: string, sampleValue: string }> => {
        try {
            if (!obj || typeof obj !== 'object') return [];
            const properties: Array<{ path: string, type: string, sampleValue: string }> = [];
            for (const [key, value] of Object.entries(obj)) {
                let type: string = typeof value;
                let sampleValue: string;
                if (Array.isArray(value)) {
                    type = 'array';
                    sampleValue = `${value.length} items`;
                    if (value.length > 0 && typeof value[0] === 'object') sampleValue = `${value.length} objects`;
                } else if (value === null) {
                    type = 'null';
                    sampleValue = 'null';
                } else if (typeof value === 'object') {
                    type = 'object';
                    try { sampleValue = `${Object.keys(value).length} properties`; } catch { sampleValue = 'object'; }
                } else if (typeof value === 'string' && value.length > 30) {
                    sampleValue = `${value.substring(0, 30)}...`;
                } else {
                    try { sampleValue = String(value); } catch { sampleValue = '?'; }
                }
                properties.push({ path: key, type, sampleValue });
            }
            return properties.sort((a, b) => a.path.localeCompare(b.path));
        } catch (error) {
            console.error('Error in getTopLevelProperties:', error);
            return [];
        }
    };

    const getNestedPropertyValue = (obj: any, propertyPath: string): any => {
        if (!propertyPath) return obj;

        const keys = propertyPath.split('.');
        let current = obj;

        for (const key of keys) {
            if (current && typeof current === 'object' && key in current) {
                current = current[key];
            } else {
                return undefined;
            }
        }

        return current;
    };

    const createSummaryData = (data: any, depth: number = 0): any => {
        // Prevent infinite recursion
        if (depth > 3) {
            return '[Max depth reached]';
        }

        if (data === null || data === undefined) {
            return data;
        }

        if (Array.isArray(data)) {
            if (data.length === 0) {
                return '[empty array]';
            }

            // Check if it's an array of objects with common properties
            const firstItem = data[0];
            if (typeof firstItem === 'object' && firstItem !== null) {
                if (firstItem.$type || firstItem.Id || firstItem.DisplayName || firstItem.Name) {
                    // It's likely a reference array - show summary info
                    const types = [...new Set(data.map((item: any) => item.$type || item.EntityType || 'unknown'))];
                    const typeInfo = types.length === 1 ? types[0] : `${types.length} types`;
                    return `[${data.length} ${typeInfo} references]`;
                } else {
                    return `[${data.length} objects]`;
                }
            }

            return `[${data.length} items]`;
        }

        if (typeof data === 'object') {
            // Collapse nested reference-like objects, but keep the top-level entity expanded.
            if (depth > 0 && (data.DisplayName || data.Id || data.$ref)) {
                return {
                    _type: 'reference',
                    DisplayName: data.DisplayName,
                    Id: data.Id,
                    $ref: data.$ref,
                    _summary: (() => {
                        try {
                            return `Reference object with ${Object.keys(data).length} properties`;
                        } catch (error) {
                            return 'Reference object (error getting keys)';
                        }
                    })()
                };
            }

            const summary: any = {};
            for (const [key, value] of Object.entries(data)) {
                if (Array.isArray(value)) {
                    summary[key] = createSummaryData(value, depth + 1);
                } else if (typeof value === 'object' && value !== null) {
                    summary[key] = createSummaryData(value, depth + 1);
                } else {
                    summary[key] = value;
                }
            }
            return summary;
        }

        return data;
    };

    // Render functions for each tab
    const formatValue = (value: any, maxLength: number = 100): string => {
        if (value === null) return 'null';
        if (value === undefined) return 'undefined';
        if (typeof value === 'string') {
            if (value.length > maxLength) {
                return `${value.substring(0, maxLength)}... (${value.length} chars)`;
            }
            return value;
        }
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        if (typeof value === 'number') return value.toString();
        if (Array.isArray(value)) {
            if (value.length === 0) return '[] (empty)';
            if (value.length <= 3) {
                return `[${value.map(v => formatValue(v, 50)).join(', ')}]`;
            }
            return `[${value.length} items: ${formatValue(value[0], 30)}, ${formatValue(value[1], 30)}, ...]`;
        }
        if (typeof value === 'object') {
            const keys = Object.keys(value);
            if (keys.length === 0) return '{} (empty)';
            if (keys.length <= 3) {
                return `{${keys.join(', ')}}`;
            }
            return `{${keys.length} properties: ${keys.slice(0, 3).join(', ')}, ...}`;
        }
        return String(value);
    };

    const getValueType = (value: any): string => {
        if (value === null) return 'null';
        if (Array.isArray(value)) {
            if (value.length === 0) return 'array (empty)';
            const firstType = typeof value[0];
            return `array[${firstType}] (${value.length} items)`;
        }
        if (typeof value === 'object') {
            const keys = Object.keys(value);
            return `object (${keys.length} properties)`;
        }
        return typeof value;
    };

    const renderBasicInfoTab = () => (
        <Box p="md">
            <Text fw={600} size="md" mb="md">Basic Entity Information</Text>
            <Text size="sm">
                <strong>Entity Type:</strong> {(() => {
                    try {
                        if (typeof entityType === 'string') return entityType;
                        if (typeof entityType === 'object' && entityType !== null) {
                            const entityTypeObj = entityType as any;
                            if (entityTypeObj.DisplayName) return entityTypeObj.DisplayName;
                            if (entityTypeObj.Id) return entityTypeObj.Id;
                            return 'Object (not string)';
                        }
                        return String(entityType || 'Unknown');
                    } catch (error) {
                        console.error('Error rendering entityType:', error);
                        return 'Error';
                    }
                })()}
            </Text>
            <Text size="sm">
                <strong>Entity Keys:</strong> {(() => {
                    try {
                        if (!entity || typeof entity !== 'object') return '0';
                        return Object.keys(entity).length.toString();
                    } catch (error) {
                        console.error('Error counting entity keys:', error);
                        return 'Error';
                    }
                })()} properties
            </Text>
            <Text size="sm">
                <strong>DisplayName:</strong> {(() => {
                    try {
                        const value = entity?.DisplayName;
                        if (value === null || value === undefined) return '-';
                        if (typeof value === 'string') return value;
                        if (typeof value === 'object' && value.DisplayName) return value.DisplayName;
                        return String(value);
                    } catch (error) {
                        console.error('Error rendering DisplayName:', error);
                        return 'Error';
                    }
                })()}
            </Text>
            <Text size="sm">
                <strong>Id:</strong> {(() => {
                    try {
                        const value = entity?.Id;
                        if (value === null || value === undefined) return '-';
                        if (typeof value === 'string') return value;
                        if (typeof value === 'object' && value.Id) return value.Id;
                        return String(value);
                    } catch (error) {
                        console.error('Error rendering Id:', error);
                        return 'Error';
                    }
                })()}
            </Text>
            <Text size="sm">
                <strong>EntityType:</strong> {(() => {
                    try {
                        const value = entity?.EntityType;
                        if (value === null || value === undefined) return '-';
                        if (typeof value === 'string') return value;
                        if (typeof value === 'object') {
                            if (value.DisplayName) return value.DisplayName;
                            if (value.Id) return value.Id;
                            try {
                                return `Object with keys: ${Object.keys(value).join(', ')}`;
                            } catch (error) {
                                console.error('Error getting object keys:', error);
                                return 'Object (error getting keys)';
                            }
                        }
                        return String(value);
                    } catch (error) {
                        console.error('Error rendering EntityType:', error);
                        return 'Error';
                    }
                })()}
            </Text>
        </Box>
    );

    const renderItemizedTab = () => {
        try {
            if (!entity || typeof entity !== 'object') {
                return (
                    <Box p="md">
                        <Alert color="blue">No entity data available</Alert>
                    </Box>
                );
            }

            const topLevelKeys = Object.keys(entity).filter(key => !key.startsWith('_'));

            // Categorize properties
            const basicProperties = topLevelKeys.filter(key => {
                const value = entity[key];
                return value !== null && value !== undefined &&
                    typeof value !== 'object' && !Array.isArray(value);
            });

            const arrayProperties = topLevelKeys.filter(key => Array.isArray(entity[key]));
            const objectProperties = topLevelKeys.filter(key => {
                const value = entity[key];
                return value !== null && typeof value === 'object' && !Array.isArray(value);
            });

            return (
                <Box p="md">
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                        {/* Summary Cards */}
                        <Paper withBorder p="md" h="100%">
                            <Group gap="xs" mb="md">
                                <IconInfoCircle size={20} color="var(--mantine-color-blue-6)" />
                                <Text fw={600} size="md">Entity Information</Text>
                            </Group>

                            <Stack gap="xs">
                                <Text size="sm">
                                    <strong>Entity Type:</strong> {(() => {
                                        try {
                                            if (typeof entityType === 'string') return entityType;
                                            if (typeof entityType === 'object' && entityType !== null) {
                                                const entityTypeObj = entityType as any;
                                                if (entityTypeObj.DisplayName) return entityTypeObj.DisplayName;
                                                if (entityTypeObj.Id) return entityTypeObj.Id;
                                                return 'Object (not string)';
                                            }
                                            return String(entityType || 'Unknown');
                                        } catch (error) {
                                            return 'Error';
                                        }
                                    })()}
                                </Text>
                                <Text size="sm">
                                    <strong>Total Properties:</strong> {topLevelKeys.length}
                                </Text>
                                <Text size="sm">
                                    <strong>Basic Properties:</strong> {basicProperties.length}
                                </Text>
                                <Text size="sm">
                                    <strong>Array Properties:</strong> {arrayProperties.length}
                                </Text>
                                <Text size="sm">
                                    <strong>Object Properties:</strong> {objectProperties.length}
                                </Text>
                            </Stack>

                            <Divider my="md" />

                            {getSchemas().length > 0 && (
                                <Box>
                                    <Text size="sm" c="dimmed" mb="xs">
                                        <strong>Schemas ({getSchemas().length}):</strong>
                                    </Text>
                                    <Group gap="xs" wrap="wrap">
                                        {getSchemas().map((schema: string, index: number) => (
                                            <Badge key={index} variant="outline" size="sm" leftSection={<IconSchema size={12} />}>
                                                {schema}
                                            </Badge>
                                        ))}
                                    </Group>
                                </Box>
                            )}
                        </Paper>

                        <Paper withBorder p="md" h="100%">
                            <Group gap="xs" mb="md">
                                <IconLink size={20} color="var(--mantine-color-blue-6)" />
                                <Text fw={600} size="md">Object References</Text>
                            </Group>

                            <Text size="sm" c="dimmed" mb="xs">
                                Found {getObjectReferencesCount()} object reference(s):
                            </Text>

                            {getObjectReferences().length > 0 ? (
                                <Group gap="xs" wrap="wrap">
                                    {getObjectReferences().map((ref: string, index: number) => (
                                        <Badge key={index} variant="outline" color="gray">
                                            {ref}
                                        </Badge>
                                    ))}
                                </Group>
                            ) : (
                                <Text size="sm" c="dimmed">
                                    No object references found
                                </Text>
                            )}
                        </Paper>

                        {/* Detailed Properties Table */}
                        <Box style={{ gridColumn: '1 / -1' }}>
                            <Paper withBorder p="md">
                                <Group gap="xs" mb="md">
                                    <IconList size={20} color="var(--mantine-color-blue-6)" />
                                    <Text fw={600} size="md">All Properties</Text>
                                </Group>

                                <Table.ScrollContainer minWidth={400}>
                                    <Table striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th><strong>Property</strong></Table.Th>
                                                <Table.Th><strong>Type</strong></Table.Th>
                                                <Table.Th><strong>Value</strong></Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {topLevelKeys.map((key) => {
                                                const value = entity[key];
                                                return (
                                                    <Table.Tr key={key}>
                                                        <Table.Td>
                                                            <Text size="sm" ff="monospace" fw={500}>
                                                                {key}
                                                            </Text>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Badge
                                                                size="sm"
                                                                color={
                                                                    Array.isArray(value) ? 'blue' :
                                                                        typeof value === 'object' ? 'gray' :
                                                                            'default'
                                                                }
                                                            >
                                                                {getValueType(value)}
                                                            </Badge>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {Array.isArray(value) && value.length > 0 ? (
                                                                <Accordion>
                                                                    <Accordion.Item value={`arr-${key}`}>
                                                                        <Accordion.Control>
                                                                            <Text size="sm">
                                                                                {value.length} item{value.length !== 1 ? 's' : ''}
                                                                            </Text>
                                                                        </Accordion.Control>
                                                                        <Accordion.Panel>
                                                                            <Box style={{ maxHeight: '300px', overflow: 'auto' }}>
                                                                                {value.map((item: any, idx: number) => (
                                                                                    <Box key={idx} p="xs" mb="xs" bg="var(--mantine-color-default)" style={{ borderRadius: 4 }}>
                                                                                        <Text size="xs" c="dimmed">
                                                                                            Item {idx + 1}:
                                                                                        </Text>
                                                                                        <Text size="sm" ff="monospace" fz="sm">
                                                                                            {typeof item === 'object' ? JSON.stringify(item, null, 2) : formatValue(item)}
                                                                                        </Text>
                                                                                    </Box>
                                                                                ))}
                                                                            </Box>
                                                                        </Accordion.Panel>
                                                                    </Accordion.Item>
                                                                </Accordion>
                                                            ) : typeof value === 'object' && value !== null ? (
                                                                <Accordion>
                                                                    <Accordion.Item value={`obj-${key}`}>
                                                                        <Accordion.Control>
                                                                            <Text size="sm">
                                                                                {Object.keys(value).length} propert{Object.keys(value).length !== 1 ? 'ies' : 'y'}
                                                                            </Text>
                                                                        </Accordion.Control>
                                                                        <Accordion.Panel>
                                                                            <Box style={{ maxHeight: '300px', overflow: 'auto' }}>
                                                                                <Text component="pre" m={0} fz="sm">
                                                                                    {JSON.stringify(value, null, 2)}
                                                                                </Text>
                                                                            </Box>
                                                                        </Accordion.Panel>
                                                                    </Accordion.Item>
                                                                </Accordion>
                                                            ) : (
                                                                <Text
                                                                    size="sm"
                                                                    ff="monospace"
                                                                    style={{ wordBreak: 'break-word' }}
                                                                >
                                                                    {formatValue(value)}
                                                                </Text>
                                                            )}
                                                        </Table.Td>
                                                    </Table.Tr>
                                                );
                                            })}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>
                            </Paper>
                        </Box>
                    </SimpleGrid>
                </Box>
            );
        } catch (error) {
            return (
                <Box p="md" c="red">
                    <Text fw={600} size="md" c="red">
                        Error rendering Itemized tab
                    </Text>
                    <Text size="sm">
                        {error instanceof Error ? error.message : 'Unknown error occurred'}
                    </Text>
                </Box>
            );
        }
    };

    const renderOverviewTab = () => {
        try {
            return (
                <JsonViewer
                    data={createSummaryData(entity)}
                    collapseObjectsByDefault
                    height="600px"
                />
            );
        } catch (error) {
            return (
                <Box p="md" c="red">
                    <Text fw={600} size="md" c="red">
                        Error rendering Overview tab
                    </Text>
                    <Text size="sm">
                        {error instanceof Error ? error.message : 'Unknown error occurred'}
                    </Text>
                </Box>
            );
        }
    };

    const renderDrillTab = () => {
        try {
            const targetEntitlements = getTargetEntitlementsList(targetEntitlement ?? null);
            const getTier3ForPath = (path: string) => {
                const norm = (s: string) => (s || '').trim().toLowerCase();
                const pathNorm = path.trim().toLowerCase();
                const pathFirst = path.split('.')[0]?.trim().toLowerCase() ?? '';
                return targetEntitlements.find((te: any) => {
                    const rawId = (te?.Attribute?.Id ?? te?.Attribute?.DisplayName ?? '').toString().trim();
                    const idNorm = norm(rawId);
                    const idWithoutPrefix = rawId.startsWith('$.') ? rawId.slice(2).toLowerCase() : idNorm;
                    return idNorm === pathNorm || idNorm === pathFirst || idWithoutPrefix === pathNorm || idWithoutPrefix === pathFirst;
                }) ?? null;
            };

            const topLevelProps = getTopLevelProperties(entity);
            const primaryValue = selectedProperty ? getNestedPropertyValue(entity, selectedProperty) : undefined;
            const isArray = Array.isArray(primaryValue);
            const isObject = primaryValue != null && typeof primaryValue === 'object' && !Array.isArray(primaryValue);
            const MAX_INDEX_OPTIONS = 100;
            const arrayIndices = isArray && primaryValue.length > 0
                ? Array.from({ length: Math.min(primaryValue.length, MAX_INDEX_OPTIONS) }, (_, i) => String(i))
                : [];
            const nestedKeys = isObject ? Object.keys(primaryValue) : [];

            const resolvedPath = selectedSubPath ? `${selectedProperty}.${selectedSubPath}` : selectedProperty;
            const displayValue = resolvedPath ? getNestedPropertyValue(entity, resolvedPath) : primaryValue;

            const propertySelectData = [
                { value: '', label: 'Select a property to drill into...' },
                ...topLevelProps.map((p) => ({
                    value: p.path,
                    label: `${p.path} (${p.type} - ${p.sampleValue})`
                }))
            ];

            const indexSelectData = [
                { value: '', label: '— View full array —' },
                ...arrayIndices.map((idx) => ({
                    value: idx,
                    label: `Index ${idx}${primaryValue.length > MAX_INDEX_OPTIONS && Number(idx) === MAX_INDEX_OPTIONS - 1 ? ` (showing first ${MAX_INDEX_OPTIONS})` : ''}`
                }))
            ];

            const nestedSelectData = [
                { value: '', label: '— View full object —' },
                ...nestedKeys.map((k) => ({ value: k, label: k }))
            ];

            return (
                <Box>
                    <Select
                        label="Select Property"
                        placeholder="Select a property to drill into..."
                        value={selectedProperty}
                        data={propertySelectData}
                        onChange={(v) => {
                            setSelectedProperty(v ?? '');
                            setSelectedSubPath('');
                        }}
                        mb="md"
                    />

                    {selectedProperty && isArray && arrayIndices.length > 0 && (
                        <Select
                            label="Select index"
                            value={selectedSubPath}
                            data={indexSelectData}
                            onChange={(v) => setSelectedSubPath(v ?? '')}
                            mb="md"
                        />
                    )}

                    {selectedProperty && isObject && nestedKeys.length > 0 && (
                        <Select
                            label="Select nested property"
                            value={selectedSubPath}
                            data={nestedSelectData}
                            onChange={(v) => setSelectedSubPath(v ?? '')}
                            mb="md"
                        />
                    )}

                    {resolvedPath && (() => {
                        const tier3Node = getTier3ForPath(resolvedPath);
                        if (tier3Node == null) return null;
                        return (
                            <Accordion mb="md">
                                <Accordion.Item value="ent">
                                    <Accordion.Control>
                                        <Text size="sm" fw={500}>Entitlements for {resolvedPath}</Text>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <JsonViewer data={tier3Node} />
                                    </Accordion.Panel>
                                </Accordion.Item>
                            </Accordion>
                        );
                    })()}

                    {selectedProperty && (
                        <Box
                            bg="var(--mantine-color-default)"
                            p="md"
                            ff="monospace"
                            fz="xs"
                            style={{
                                maxHeight: '600px',
                                overflow: 'auto',
                                border: '1px solid var(--mantine-color-default-border)',
                                borderRadius: 4
                            }}
                        >
                            <Text size="sm" c="dimmed" mb="xs">
                                {resolvedPath !== selectedProperty ? (
                                    <>Property: <strong>{resolvedPath}</strong></>
                                ) : (
                                    <>Property: <strong>{selectedProperty}</strong></>
                                )}
                            </Text>
                            {(() => {
                                const value = displayValue;
                                const isRef = value != null && typeof value === 'object' && !Array.isArray(value) &&
                                    ((value.$ref != null && value.$ref !== '') || ((value.Id != null || value.id != null) && (value.Type != null || value.type != null || value.EntityType != null)));
                                return (
                                    <>
                                        {isRef && (
                                            <Group gap="xs" wrap="wrap" mb="md">
                                                <Text size="xs" c="dimmed">Open reference:</Text>
                                                <ScimReferenceDisplay reference={value} showLink showType />
                                            </Group>
                                        )}
                                        <pre>{(() => {
                                            try {
                                                return value !== undefined ? JSON.stringify(value, null, 2) : `Error reading: ${resolvedPath}`;
                                            } catch (error) {
                                                return `Error serializing: ${error instanceof Error ? error.message : 'Unknown error'}`;
                                            }
                                        })()}</pre>
                                    </>
                                );
                            })()}
                        </Box>
                    )}
                </Box>
            );
        } catch (error) {
            return (
                <Box p="md" c="red">
                    <Text fw={600} size="md" c="red">
                        Error rendering Drill tab
                    </Text>
                    <Text size="sm">
                        {error instanceof Error ? error.message : 'Unknown error occurred'}
                    </Text>
                </Box>
            );
        }
    };

    const renderEntTab = () => (
        <Box p="md">
            <Tabs value={entSubTab} onChange={setEntSubTab} mb="md" keepMounted={false}>
                <Tabs.List>
                    <Tabs.Tab value="0">Tier 1</Tabs.Tab>
                    <Tabs.Tab value="1">Tier 2</Tabs.Tab>
                    <Tabs.Tab value="2">Tier 3</Tabs.Tab>
                    <Tabs.Tab value="3">Bindings</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="0">
                    <Text size="sm" c="dimmed" mb="xs">
                        GET /mgmt/Entitlements/entity (entity types / nav)
                    </Text>
                    {(() => {
                        const tier1 = EntitlementsService.getCachedEntityEntitlements();
                        if (tier1 == null) {
                            return (
                                <Alert color="blue">
                                    No tier 1 data in cache. Reload the app or use a table to load entitlements.
                                </Alert>
                            );
                        }
                        return (
                            <JsonViewer data={tier1} title="Tier 1" />
                        );
                    })()}
                </Tabs.Panel>
                <Tabs.Panel value="1">
                    <Text size="sm" c="dimmed" mb="xs">
                        {`GET /mgmt/Entitlements/entity/${effectiveEntityType}`}
                    </Text>
                    <Tier2SchemaTabView entityType={effectiveEntityType} />
                    {(() => {
                        const tier2 = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(effectiveEntityType);
                        if (tier2 == null) return null;
                        return (
                            <Box mt="md">
                                <JsonViewer data={tier2} title="Full response" collapseObjectsByDefault />
                            </Box>
                        );
                    })()}
                </Tabs.Panel>
                <Tabs.Panel value="2">
                    <Text size="sm" c="dimmed" mb="xs">
                        {entityId
                            ? `GET /mgmt/Entitlements/entity/${effectiveEntityType.toLowerCase()}/${entityId}`
                            : 'Tier 3 (target entitlement) requires an entity id.'}
                    </Text>
                    {targetEntitlement != null && typeof targetEntitlement === 'object' ? (
                        <JsonViewer data={targetEntitlement} title="Tier 3" collapseObjectsByDefault />
                    ) : (
                        <Alert color="blue">
                            No tier 3 data (target entitlement). It is loaded with the entity when viewing a specific item.
                        </Alert>
                    )}
                </Tabs.Panel>
                <Tabs.Panel value="3">
                    <Text size="sm" c="dimmed" mb="xs">
                        Tier 3 attributes with Binding (or legacy Bindings / RegEx, Required, AllowedValues).
                    </Text>
                    {getTargetEntitlementsList(targetEntitlement ?? null).length > 0 ? (
                        (() => {
                            const bindings = getTargetEntitlementsList(targetEntitlement ?? null)
                                .filter(hasAnyBindingProperty)
                                .map(toBindingView);
                            if (bindings.length === 0) {
                                return (
                                    <Alert color="blue">
                                        No tier 3 attributes have Binding or legacy binding properties.
                                    </Alert>
                                );
                            }
                            const bindingId = (b: Record<string, unknown>) => String(b.Id ?? b.id ?? '');
                            const bindingSelectData = [
                                { value: '', label: `All attributes (${bindings.length})` },
                                ...bindings.map((b: Record<string, unknown>) => {
                                    const id = bindingId(b);
                                    const label = (b.DisplayName ?? b.displayName ?? b.Id ?? id) as string;
                                    return { value: id, label };
                                })
                            ];
                            const selectedBinding = selectedBindingId
                                ? bindings.find((b: Record<string, unknown>) => bindingId(b) === selectedBindingId)
                                : null;
                            const payload = selectedBinding != null ? selectedBinding : bindings;
                            return (
                                <>
                                    <Select
                                        label="Attribute"
                                        value={selectedBindingId}
                                        data={bindingSelectData}
                                        onChange={(v) => setSelectedBindingId(v ?? '')}
                                        mb="md"
                                        miw={260}
                                    />
                                    <JsonViewer data={payload} title={selectedBinding ? 'Attribute' : 'All attributes'} />
                                </>
                            );
                        })()
                    ) : (
                        <Alert color="blue">
                            No tier 3 data. Open an item to load target entitlement, then check Ent → Bindings.
                        </Alert>
                    )}
                </Tabs.Panel>
            </Tabs>
        </Box>
    );

    const renderRawJsonTab = () => {
        try {
            // Safe JSON serialization that handles circular references but preserves all properties
            const safeStringify = (obj: any, space?: number): string => {
                try {
                    const seen = new WeakSet();

                    // Create a replacer function to handle circular references
                    const replacer = (_key: string, value: any) => {
                        // Handle circular references
                        if (typeof value === 'object' && value !== null) {
                            if (seen.has(value)) {
                                return '[Circular Reference]';
                            }
                            seen.add(value);
                        }

                        // Handle functions, undefined, symbols, etc.
                        if (typeof value === 'function') return '[Function]';
                        if (typeof value === 'undefined') return '[Undefined]';
                        if (typeof value === 'symbol') return '[Symbol]';

                        return value;
                    };

                    return JSON.stringify(obj, replacer, space);
                } catch (error) {
                    return `Error serializing JSON: ${error instanceof Error ? error.message : 'Unknown error'}`;
                }
            };

            const jsonString = safeStringify(entity, 2);
            return (
                <Paper withBorder style={{ overflow: 'hidden' }}>
                    <ScimAceEditor
                        mode="json"
                        value={jsonString}
                        readOnly={true}
                        name="shared-debug-raw-json"
                        height="500px"
                        fontSize={14}
                        showPrintMargin={false}
                        showGutter={true}
                        highlightActiveLine={false}
                        setOptions={{
                            showLineNumbers: true,
                            tabSize: 2,
                            useWorker: false,
                            showFoldWidgets: true
                        }}
                        onLoad={(editor) => {
                            window.requestAnimationFrame(() => {
                                try {
                                    const session = editor.getSession?.() ?? editor.session;
                                    session?.unfold?.();
                                    session?.foldAll?.(2);
                                    editor.gotoLine?.(1, 0, true);
                                    editor.clearSelection?.();
                                } catch {
                                    // Folding is best-effort only.
                                }
                            });
                        }}
                    />
                </Paper>
            );
        } catch (error) {
            return (
                <Box p="md" c="red">
                    <Text fw={600} size="md" c="red">
                        Error rendering Raw JSON tab
                    </Text>
                    <Text size="sm">
                        {error instanceof Error ? error.message : 'Unknown error occurred'}
                    </Text>
                </Box>
            );
        }
    };

    // Error boundary for the entire component
    if (error) {
        return (
            <Paper withBorder p="md">
                <Text fw={600} size="md" c="red">
                    Debug Component Error
                </Text>
                <Text size="sm" c="red">
                    {error}
                </Text>
                <Button
                    onClick={() => setError(null)}
                    variant="outline"
                    size="sm"
                    mt="sm"
                >
                    Retry
                </Button>
            </Paper>
        );
    }

    const renderTabPanel = (tabKey: SharedDebugTabKey) => {
        const tabValue = DEBUG_TAB_VALUES[tabKey];
        const fallbackTitle = tabKey === 'rawJson'
            ? 'Raw JSON'
            : tabKey === 'basicInfo'
                ? 'Basic Info'
                : tabKey.charAt(0).toUpperCase() + tabKey.slice(1);
        const fallback = (
            <Box p="md" c="red">
                <Text fw={600} size="md" c="red">Error in {fallbackTitle} tab</Text>
                <Text size="sm">An error occurred while rendering this tab.</Text>
            </Box>
        );

        if (tabValue === '0') {
            return <TabErrorBoundary fallback={fallback}>{renderBasicInfoTab()}</TabErrorBoundary>;
        }
        if (tabValue === '1') {
            return <TabErrorBoundary fallback={fallback}>{renderItemizedTab()}</TabErrorBoundary>;
        }
        if (tabValue === '2') {
            return <TabErrorBoundary fallback={fallback}>{renderOverviewTab()}</TabErrorBoundary>;
        }
        if (tabValue === '3') {
            return <TabErrorBoundary fallback={fallback}>{renderDrillTab()}</TabErrorBoundary>;
        }
        if (tabValue === '4') {
            return <TabErrorBoundary fallback={fallback}>{renderEntTab()}</TabErrorBoundary>;
        }
        return (
            <TabErrorBoundary fallback={fallback}>
                <Box>
                    {forcedTab === 'rawJson' && (
                        <Group justify="flex-end" mb="sm">
                            <Tooltip label="Copy JSON to clipboard">
                                <ActionIcon variant="subtle" size="sm" onClick={() => { void handleCopyJson(); }}>
                                    <IconCopy size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    )}
                    {renderRawJsonTab()}
                </Box>
            </TabErrorBoundary>
        );
    };

    // Safety check for entity
    if (!entity) {
        return (
            <Paper withBorder p="md">
                <Text fw={600} size="md" c="dimmed">
                    No entity data available
                </Text>
            </Paper>
        );
    }

    try {
        const content = forcedTab ? (
            renderTabPanel(forcedTab)
        ) : (
            <Tabs value={effectiveActiveTab} onChange={handleTabChange} mb="md" keepMounted={false}>
                <Tabs.List>
                    <Tabs.Tab value="0">Basic Info</Tabs.Tab>
                    <Tabs.Tab value="1">Itemized</Tabs.Tab>
                    <Tabs.Tab value="2">Overview</Tabs.Tab>
                    <Tabs.Tab value="3">Drill</Tabs.Tab>
                    <Tabs.Tab value="4">Ent</Tabs.Tab>
                    <Tabs.Tab value={String(RAW_JSON_TAB_INDEX)}>
                        <Group gap="xs">
                            Raw JSON
                            {effectiveActiveTab === String(RAW_JSON_TAB_INDEX) && (
                                <Tooltip label="Copy JSON to clipboard">
                                    <ActionIcon
                                        variant="subtle"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void handleCopyJson();
                                        }}
                                    >
                                        <IconCopy size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="0">{renderTabPanel('basicInfo')}</Tabs.Panel>
                <Tabs.Panel value="1">{renderTabPanel('itemized')}</Tabs.Panel>
                <Tabs.Panel value="2">{renderTabPanel('overview')}</Tabs.Panel>
                <Tabs.Panel value="3">{renderTabPanel('drill')}</Tabs.Panel>
                <Tabs.Panel value="4">{renderTabPanel('ent')}</Tabs.Panel>
                <Tabs.Panel value={String(RAW_JSON_TAB_INDEX)}>{renderTabPanel('rawJson')}</Tabs.Panel>
            </Tabs>
        );

        if (bare) {
            return <>{content}</>;
        }

        return (
            <Paper withBorder p="md">
                {title && (
                    <Group gap="xs" mb="md">
                        <IconCode size={20} color="var(--mantine-color-blue-6)" />
                        <Text fw={600} size="md">{title}</Text>
                    </Group>
                )}
                {content}
            </Paper>
        );
    } catch (error) {
        console.error('Debug component error:', error);
        setError(error instanceof Error ? error.message : 'Unknown error occurred');
        return (
            <Paper withBorder p="md">
                <Text fw={600} size="md" c="red">
                    Debug Component Error
                </Text>
                <Text size="sm" c="red">
                    {error instanceof Error ? error.message : 'Unknown error occurred'}
                </Text>
                <Button
                    onClick={() => setError(null)}
                    variant="outline"
                    size="sm"
                    mt="sm"
                >
                    Retry
                </Button>
            </Paper>
        );
    }
};
